<template>
  <div>
    <span class="task-edition__text">Дата создания: {{ initialTask.createdAt }}</span>
    <form v-on:submit.prevent="onEdit" class="task-edition">
      <input-text-length-limitation
        class="task-edition__input-limitation"
        title="Заголовок"
        v-bind:max-length="200"
        v-bind:violations="getViolationsBySource('task.title')"
        v-model="title"
      />
      <autocomplete-input
        class="task-edition__input"
        clearable
        label="Роль"
        v-model="roleSlug"
        item-text="title"
        item-value="slug"
        required="required"
        v-on:input="onSelectRoleSlug"
        v-bind:items="rolesOptions"
        v-bind:violations="getViolationsBySource('task.role')"
      />
      <autocomplete-input
        class="task-edition__input"
        clearable
        label="Исполнитель"
        v-model="performerId"
        item-text="title"
        item-value="value"
        v-bind:items="performers"
        v-bind:violations="getViolationsBySource('task.performer')"
      />
      <autocomplete-input
        attach
        class="task-edition__inputt multiline"
        hide-details
        item-text="title"
        item-value="value"
        label="ФИО пациента"
        v-bind:clearable="true"
        v-bind:violations="getViolationsBySource('task.patient')"
        v-bind:items="patients"
        v-bind:search-input.sync="patientSearchQuery"
        v-bind:value="patientId"
        v-on:search-input="onUpdateSearch"
        v-on:blur="onBlurInputPatient"
        v-on:input="onInputPatient"
      />
      <div class="task-edition__fields-wrapper">
        <div class="task-edition__data">
          <div class="task-edition__data__label">Дедлайн</div>
          <date-picker-dialog
            v-bind:allowed-dates="allowedDates"
            v-bind:value="deadLineDate"
            v-on:input="setNewDate"
          >
            <template v-slot:trigger="{ listenerObject }">
              <button
                title="Сменить дату"
                v-if="deadLineDate"
                type="button"
                v-on="listenerObject"
              >
                {{ deadLineTitle }}
              </button>
            </template>
          </date-picker-dialog>
        </div>
        <div class="task-edition__status">
          <the-select
            title="Статус"
            v-model="status"
            v-bind:options="$options.statuses"
            v-bind:violations="getViolationsBySource('task.status')"
          />
        </div>
      </div>
      <textarea-length-limitation
        placeholder="Подробное описание"
        title="Подробное описание"
        v-model="description"
        v-bind:max-length="300"
        v-bind:violations="getViolationsBySource('task.description')"
      />
      <div class="task-edition__control-buttons">
        <button
          class="task-edition__cancel"
          type="button"
          v-on:click="() => $emit('cancel')"
        >Отмена</button>
        <button
          class="task-edition__submit"
          type="submit"
        >Сохранить</button>
      </div>
    </form>
  </div>
</template>

<script>

import DatePickerDialog from '@/components/DatePickerDialog.vue';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import TheSelect from '@/components/TheSelect.vue';
import { TaskRequest, statuses, TaskForEdit } from '@/storage/gateways/TaskGateway';
import { getISODateStringByDate } from '@/mixins/dateMixin';
import debounce from 'lodash.debounce';
import InputTextLengthLimitation from '@/components/InputTextLengthLimitation.vue';
import AutocompleteInput from '@/components/AutocompleteInput.vue';

export default {
  name: 'FormTaskEditing',
  components: {
    AutocompleteInput,
    InputTextLengthLimitation,
    TheSelect,
    TextareaLengthLimitation,
    DatePickerDialog,
  },
  data() {
    const deadLine = this.initialTask.deadLine.split('.').reverse().join('-');
    const currentDate = new Date(getISODateStringByDate(new Date()));
    return {
      /**
       * @description Запрос на поиск пациента.
       * @type {string}
       */
      patientSearchQuery: '',
      currentDate,
      deadLine,
      deadLineDate: new Date(deadLine),
      description: this.initialTask.description,
      id: this.initialTask.id,
      performerId: this.initialTask.performerId,
      /**
       * @description Слаг роли
       * @type {string}
       */
      roleSlug: this.initialTask.clinicRoleSlug,
      /**
       * @description Исполнители
       * @type Array
       */
      performers: this.initialPerformers,
      status: this.initialTask.status,
      title: this.initialTask.title,
      patientId: this.initialTask.patientId,
    };
  },
  methods: {
    /**
     * @description Обработать событие ввода поиска по пациентам.
     * @param {string} query
     * @returns {void}
     */
    onUpdateSearch(query) {
      this.$emit('search-input', query);
    },
    /**
     * @description  Обработать выбор слага роли
     * @param {string} role
     * @returns {void}
     */
    onSelectRoleSlug(role) {
      this.performers = this.initialPerformers.filter((item) => item.role_slug === role);
    },
    /**
     * @description Обработать изменение пациента.
     * @param {?number} value
     * @returns {void}
     */
    onInputPatient(value) {
      this.patientId = value;
      if (value && this.isPatientFieldInvalid) {
        this.isPatientFieldInvalid = false;
      }
    },
    /**
     * @description Обработать потерю фокуса с поля выбора пациента.
     * @returns {void}
     */
    onBlurInputPatient() {
      this.isPatientFieldInvalid = !this.patientId && this.patientSearchQuery;
    },
    /**
     * @description Функция описывающая условие доступности выбора дат в datepicker
     * @param {String} candidate Строка с датой в формате Y-m-d
     * @return {boolean}
     */
    allowedDates(candidate) {
      return (new Date(candidate)) >= this.currentDate;
    },
    /**
     * @description Инициализировать событие поднятие статуса
     * @param val
     */
    dispatchTaskIsChanged(val) {
      this.$emit('task-is-changed', val);
    },
    /**
     * @description Отобразить причины отказа в исполнении запроса
     * @param source
     * @return {*}
     */
    getViolationsBySource(source) {
      return this.violations?.[source] || [];
    },
    /**
     * @description Событие запроса на редактирование
     */
    onEdit() {
      const request = new TaskRequest(
        this.title,
        this.performerId ?? null,
        this.deadLine,
        this.status,
        this.description,
        this.patientId,
        this.roleSlug,
      );
      this.$emit('edit-task', request, this.id);
    },
    /**
     * @description Установить новую дату
     * @param {Date} date
     */
    setNewDate(date) {
      this.deadLine = getISODateStringByDate(date);
      this.deadLineDate = date;
    },
  },
  computed: {
    /**
     * @description Состояние не соответствует начальному
     * @return {boolean}
     */
    taskIsChanged() {
      const initialDeadLine = this.initialTask.deadLine.split('.').reverse().join('-');
      return !(this.deadLine === initialDeadLine
        && this.description === this.initialTask.description
        && this.performerId === this.initialTask.performerId
        && this.status === this.initialTask.status
        && this.title === this.initialTask.title
        && this.patientId === this.initialTask.patientId);
    },
    /**
     * @description Заголовок крайнего срока
     * @return {string}
     */
    deadLineTitle() {
      const options = {
        day: 'numeric',
        month: 'long',
      };
      const weekday = this.deadLineDate.toLocaleDateString('ru-RU', { weekday: 'short' });
      return `${this.deadLineDate.toLocaleDateString('ru-RU', options)}, ${weekday.toUpperCase()}`;
    },
  },
  watch: {
    taskIsChanged: {
      handler: debounce(function _(val) {
        this.dispatchTaskIsChanged(val);
      }, 300),
      deep: true,
    },
  },
  props: {
    /**
     * @description Начальные исполнители
     * @type Array
     */
    initialPerformers: { type: Array, default: () => [] },
    /**
     * @description Опции ролей
     * @type Array
     */
    rolesOptions: { type: Array, default: () => [], required: true },
    patients: { type: Array, default: () => [] },
    initialTask: { type: TaskForEdit, required: true },
    violations: { type: Object, default: () => {} },
  },
  statuses,
};
</script>

<style scoped lang="scss">
@use "@/scss/buttons";
.task-edition {
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 20px;
  &__text {
    display: flex;
    font-weight: 400;
    font-size: 14px;
    line-height: 17px;
    color: #192A3E;
    margin: 5px 0 0 0;
  }
  &__fields-wrapper {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px 10px;
  }
  &__input {
    ::v-deep .v-autocomplete {
      margin: 0;
    }
  }
  &__data {
    &__label {
      font-weight: 400;
      font-size: 12px;
      line-height: 16px;
      color: #90A0B7;
      margin: 0 0 3px 0;
    }
    button {
      padding: 7px 50px 7px 10px;
      width: 100%;
      font-size: 16px;
      text-align: left;
      border: 1px solid #C2CFE0;
      border-radius: 4px;
      background-image: url("@/assets/img/calendar-grey.svg");
      background-position: right 10px center;
    }
  }
  &__submit {
    @include buttons.btn-blue(10rem, 3rem, 1rem);
  }
  &__cancel {
    @include buttons.btn-transparent(10rem, 3rem, 1rem);
  }
  &__control-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-gap: 10px;
    max-width: 330px;
    width: 100%;
    margin: 30px 0 0 auto;
  }
  &__button {
    @include buttons.btn-blue(215px, 56px, 20px);
    margin: 100px 0 0 auto;
  }
}
@media screen and (max-width: 480px) {
  .task-edition {
    &__fields-wrapper {
      grid-template-columns: 1fr;
    }
  }
}

@media screen and (max-width: 380px) {
  .task-edition {
    &__control-buttons {
      grid-template-columns: 1fr;
    }
    &__cancel,
    &__submit {
      width: 100%;
    }
  }
}
</style>
